import { atom } from 'jotai';
import { LABELLING_TYPE } from '../../../common/constant/constant';
import type { RemainingItemsDetailsT } from '../../../modernization-common/side-panel-m4/types';
import { REMAINING_ITEM_OBJECT, REMAINING_ITEMS_TYPES, RemainingItemsErrorType } from '../../../modernization-common/side-panel-m4/types';
import type { ImageQuadrantValidationConfig } from '../../labelling/types';
import type { AnnotationDataT, CuboidDataT, EllipseDataT, PointDataT, PolygonDataT, PolylineDataT, RectDataT, SkeletonDataT } from '../../labelling/types/annotations.type';
import { annotationMetadataAtom, currentFrameLoadedAnnotationsAtom, frameCountAtom } from './annotation-atoms';
import { fileTypeAtom, metadataAtom } from './file-atoms';
export const QUADRANT_IMBALANCE_REMAINING_ITEM_ID = 'image-quadrant-imbalance';
export const MINIMUM_ANNOTATIONS_REMAINING_ITEM_ID = 'image-minimum-annotations';
type CentroidT = {
  cx: number;
  cy: number;
};
type AnnotationGeometryT = {
  annotationType: string;
  labelId: string;
  data: AnnotationDataT;
};
export function averagePoints(points: ReadonlyArray<readonly [number, number]>): CentroidT | null {
  if (!points.length) {
    return null;
  }
  let sx = 0;
  let sy = 0;
  for (const [x, y] of points) {
    sx += x;
    sy += y;
  }
  const n = points.length;
  return {
    cx: sx / n,
    cy: sy / n
  };
}

/**
 * Image-space centroid used for quadrant bucketing. Returns null when geometry is unknown or skipped.
 */
export function getAnnotationImageCentroid(annotation: AnnotationGeometryT): CentroidT | null {
  switch (annotation.annotationType) {
    case 'RECTANGLE':
      {
        const {
          x,
          y,
          width,
          height
        } = (annotation.data as RectDataT);
        return {
          cx: x + width / 2,
          cy: y + height / 2
        };
      }
    case 'KEY_POINT':
      {
        const {
          x,
          y
        } = (annotation.data as PointDataT);
        return {
          cx: x,
          cy: y
        };
      }
    case 'ELLIPSE':
      {
        const {
          centerX,
          centerY
        } = (annotation.data as EllipseDataT);
        return {
          cx: centerX,
          cy: centerY
        };
      }
    case 'POLYGON':
    case 'POLYLINE':
      return averagePoints((annotation.data as PolygonDataT | PolylineDataT).points);
    case 'CUBOID':
      {
        const flat: [number, number][] = [];
        for (const p of (annotation.data as CuboidDataT).points) {
          flat.push([p[0], p[1]]);
        }
        return averagePoints(flat);
      }
    case 'SKELETON':
      {
        const pts = (annotation.data as SkeletonDataT).nodes.map(n => ([n.point[0], n.point[1]] as const));
        return averagePoints(pts);
      }
    case 'SEGMENTATION':
    case 'FRAME_CLASSIFICATION':
    default:
      return null;
  }
}
export function getQuadrantIndex(cx: number, cy: number, imageWidth: number, imageHeight: number): number {
  if (!(imageWidth > 0) || !(imageHeight > 0)) {
    return -1;
  }
  const midX = imageWidth / 2;
  const midY = imageHeight / 2;
  const left = cx < midX;
  const top = cy < midY;
  if (top && left) {
    return 0;
  }
  if (top && !left) {
    return 1;
  }
  if (!top && left) {
    return 2;
  }
  return 3;
}
export function countAnnotationsByQuadrant(annotations: Record<string, AnnotationGeometryT>, imageWidth: number, imageHeight: number, annotationLabelsToExclude: string[]): number[] {
  const counts: [number, number, number, number] = [0, 0, 0, 0];
  for (const annotation of Object.values(annotations)) {
    if (shouldExcludeFromQuadrantValidation(annotation, annotationLabelsToExclude)) {
      continue;
    }
    const centroid = getAnnotationImageCentroid(annotation);
    if (!centroid) {
      continue;
    }
    const q = getQuadrantIndex(centroid.cx, centroid.cy, imageWidth, imageHeight);
    if (q === 0 || q === 1 || q === 2 || q === 3) {
      counts[q] += 1;
    }
  }
  return counts;
}
export function hasQuadrantImbalance(quadrantCounts: number[], options: {
  maxSingleQuadrantShare: number;
}): boolean {
  const maxShare = options?.maxSingleQuadrantShare;
  const total = quadrantCounts.reduce((a, b) => a + b, 0);
  if (total === 0) {
    return false;
  }
  const maxInQuadrant = Math.max(...quadrantCounts);
  return maxInQuadrant / total > maxShare;
}
export function shouldExcludeFromQuadrantValidation(annotation: AnnotationGeometryT, annotationLabelsToExclude: string[]): boolean {
  return annotationLabelsToExclude.some(label => annotation.labelId.includes(label));
}
export function getMinimumCountEligibleAnnotationCount(annotations: Record<string, AnnotationGeometryT>, annotationLabelsToExclude: string[]): number {
  return Object.values(annotations).filter(annotation => !shouldExcludeFromQuadrantValidation(annotation, annotationLabelsToExclude)).length;
}
export type ComputeQuadrantImbalanceArgs = {
  annotations: Record<string, AnnotationGeometryT>;
  imageWidth: number;
  imageHeight: number;
  config: Omit<ImageQuadrantValidationConfig, 'isEnabled'>;
};
export function computeQuadrantImbalanceRemainingItems({
  annotations,
  imageWidth,
  imageHeight,
  config
}: ComputeQuadrantImbalanceArgs): RemainingItemsDetailsT[] {
  const annotationCount = getMinimumCountEligibleAnnotationCount(annotations, config.annotationLabelsToExclude);
  if (annotationCount < config.minimumAnnotations) {
    return [{
      id: MINIMUM_ANNOTATIONS_REMAINING_ITEM_ID,
      label: 'Task',
      type: REMAINING_ITEMS_TYPES.ERROR,
      objectType: REMAINING_ITEM_OBJECT.TASK,
      errorType: RemainingItemsErrorType.MINIMUM_ANNOTATIONS,
      message: `At least ${config.minimumAnnotations} annotations are required before submitting this task. Current count: ${annotationCount}.`
    }];
  }
  const counts = countAnnotationsByQuadrant(annotations, imageWidth, imageHeight, config.annotationLabelsToExclude);
  if (!hasQuadrantImbalance(counts, {
    maxSingleQuadrantShare: config.maxSingleQuadrantSharePercent / 100
  })) {
    return [];
  }
  const total = counts.reduce((a, b) => a + b, 0);
  const maxInQuadrant = Math.max(...counts);
  const maxSharePercent = Math.round(maxInQuadrant / total * 100);
  return [{
    id: QUADRANT_IMBALANCE_REMAINING_ITEM_ID,
    label: 'Task',
    type: REMAINING_ITEMS_TYPES.ERROR,
    objectType: REMAINING_ITEM_OBJECT.TASK,
    errorType: RemainingItemsErrorType.QUADRANT_IMBALANCE,
    message: `Annotations are unevenly distributed across image quadrants (largest quadrant ~${maxSharePercent}% of ${total} placements). Spread labels more evenly.`
  }];
}
export const imageQuadrantValidationConfigAtom = atom<ImageQuadrantValidationConfig | undefined>(undefined);

/**
 * Task-level remaining-items rows when quadrant annotation share exceeds the configured cap.
 * Only runs for single-frame image tasks and only when the account gate atom is enabled.
 * Merged into `remainingItemsV2Atom` alongside rule-engine and questionnaire rows.
 */
export const imageQuadrantValidationRemainingItemsAtom = atom((get): RemainingItemsDetailsT[] => {
  const config = get(imageQuadrantValidationConfigAtom);
  if (!config?.isEnabled) {
    return [];
  }
  if (get(fileTypeAtom) !== LABELLING_TYPE.IMAGE) {
    return [];
  }
  if (get(frameCountAtom) !== 1) {
    return [];
  }
  const loadedAnnotations = get(currentFrameLoadedAnnotationsAtom);
  const metadata = get(annotationMetadataAtom);
  const annotations: Record<string, AnnotationGeometryT> = {};
  for (const [annotationId, payload] of loadedAnnotations) {
    const m = metadata.get(annotationId);
    if (m) {
      annotations[annotationId] = {
        annotationType: m.annotationType,
        labelId: m.labelId,
        data: payload.geometry
      };
    }
  }
  const {
    width,
    height
  } = get(metadataAtom);
  return computeQuadrantImbalanceRemainingItems({
    annotations,
    imageWidth: width,
    imageHeight: height,
    config: {
      minimumAnnotations: config.minimumAnnotations,
      maxSingleQuadrantSharePercent: config.maxSingleQuadrantSharePercent,
      annotationLabelsToExclude: config.annotationLabelsToExclude
    }
  });
});